import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type StaticImageData } from 'next/image';
import { type AnchorHTMLAttributes, type ComponentType, type ReactNode, type RefObject, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Button } from '@shared/ui-components/button/button';
import { Card } from '@shared/ui-components/card/card';
import { ImageWithFallback } from '@shared/ui-components/image-with-fallback/image-with-fallback';
import { GTM_ACTION_CLICK, GTM_EVENT_NAME_INTERNAL_LINK_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { CloseIcon } from '../../../svgs';
import { CountdownSpinner } from '../countdown-spinner/countdown-spinner';
import styles from './recommendation-card.module.scss';
const PARENT_CLASS = 'recommendation-card';
export type RecommendationCardProps = {
  authors: string[];
  cancelButtonRef: RefObject<HTMLButtonElement | null>;
  cmsContent: {
    autoPlayLabel: string;
    cancelLabel: string;
    playNowLabel: string;
    recommendationCardDescription: string;
    recommendationCardDescriptionSecondary: string;
  };
  contentTypeIcon?: ReactNode;
  isExternal: boolean;
  LinkComponent?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;
  onClose: () => void;
  onCountdownEnd: () => void;
  thumbnail: {
    altText?: string;
    fallbackSrc?: StaticImageData | string;
    src?: string;
  };
  title: string;
  url: string;
};
const sendGTMWatchNextClick = () => {
  sendGtmEvent({
    action: GTM_ACTION_CLICK,
    event: GTM_EVENT_USER_INTERACTIONS,
    eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
    label: 'watch_next_click'
  });
};
export const RecommendationCard = t0 => {
  const $ = _c(55);
  const {
    authors,
    cancelButtonRef,
    cmsContent,
    contentTypeIcon,
    isExternal,
    LinkComponent,
    onClose,
    onCountdownEnd,
    thumbnail,
    title,
    url
  } = t0;
  const formatTranslationString = useFormatTranslationString();
  const nextItemUrl = url;
  const [time, setTime] = useState(15);
  const {
    inView,
    ref: inViewRef
  } = useInView();
  const isTimerPaused = !inView;
  let t1;
  let t2;
  if ($[0] !== isTimerPaused || $[1] !== time) {
    t1 = () => {
      const timer = time > 0 ? setTimeout(() => {
        if (!isTimerPaused) {
          setTime(_temp);
        }
      }, 1000) : undefined;
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    };
    t2 = [isTimerPaused, time];
    $[0] = isTimerPaused;
    $[1] = time;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const t3 = isExternal ? "_blank" : undefined;
  let t4;
  if ($[4] !== nextItemUrl || $[5] !== t3) {
    t4 = {
      href: nextItemUrl,
      onClick: sendGTMWatchNextClick,
      target: t3
    };
    $[4] = nextItemUrl;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const sharedLinkProps = t4;
  let t5;
  if ($[7] !== inViewRef) {
    t5 = <span ref={inViewRef} />;
    $[7] = inViewRef;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== cmsContent.recommendationCardDescription) {
    t6 = <div aria-hidden={true} className={styles["recommendation-card-description"]}>{cmsContent.recommendationCardDescription}</div>;
    $[9] = cmsContent.recommendationCardDescription;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classNames(styles["recommendation-card-description"], styles["recommendation-card-description--secondary"]);
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== cmsContent.recommendationCardDescriptionSecondary || $[13] !== formatTranslationString || $[14] !== time) {
    t8 = formatTranslationString(cmsContent.recommendationCardDescriptionSecondary, {
      remainingTime: time
    });
    $[12] = cmsContent.recommendationCardDescriptionSecondary;
    $[13] = formatTranslationString;
    $[14] = time;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <div aria-hidden={true} className={t7}>{t8}</div>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== LinkComponent || $[19] !== isTimerPaused || $[20] !== onCountdownEnd || $[21] !== sharedLinkProps || $[22] !== thumbnail?.altText || $[23] !== thumbnail?.fallbackSrc || $[24] !== thumbnail?.src) {
    t10 = LinkComponent && <LinkComponent aria-hidden={true} className={styles[`${PARENT_CLASS}__thumbnail`]} tabIndex={-1} {...sharedLinkProps}><ImageWithFallback alt={thumbnail?.altText || ""} className={styles[`${PARENT_CLASS}__image`]} fallbackSrc={thumbnail?.fallbackSrc || ""} height={88} sizes="88px" src={thumbnail?.src || ""} width={88} /><div className={styles[`${PARENT_CLASS}__loader-container`]}><CountdownSpinner duration={15} onCountdownEnd={onCountdownEnd} paused={isTimerPaused} /></div></LinkComponent>;
    $[18] = LinkComponent;
    $[19] = isTimerPaused;
    $[20] = onCountdownEnd;
    $[21] = sharedLinkProps;
    $[22] = thumbnail?.altText;
    $[23] = thumbnail?.fallbackSrc;
    $[24] = thumbnail?.src;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== LinkComponent || $[27] !== cmsContent.playNowLabel || $[28] !== sharedLinkProps || $[29] !== title) {
    t11 = LinkComponent && <LinkComponent aria-label={cmsContent.playNowLabel} className={styles[`${PARENT_CLASS}__title`]} {...sharedLinkProps}>{title}</LinkComponent>;
    $[26] = LinkComponent;
    $[27] = cmsContent.playNowLabel;
    $[28] = sharedLinkProps;
    $[29] = title;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== contentTypeIcon) {
    t12 = contentTypeIcon && <div className={styles[`${PARENT_CLASS}__additional-details-icon`]}>{contentTypeIcon}</div>;
    $[31] = contentTypeIcon;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== authors) {
    t13 = authors !== undefined && authors.length > 0 && <><div className={styles[`${PARENT_CLASS}__additional-details-text`]}>{authors[0]}</div>{authors.length >= 2 && <span>+{authors.length}</span>}</>;
    $[33] = authors;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <div className={styles[`${PARENT_CLASS}__additional-details`]}>{t12}{t13}</div>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t11 || $[39] !== t14) {
    t15 = <div className={styles[`${PARENT_CLASS}__content`]}>{t11}{t14}</div>;
    $[38] = t11;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const t16 = cmsContent.cancelLabel;
  let t17;
  if ($[41] !== cmsContent.autoPlayLabel || $[42] !== formatTranslationString) {
    t17 = formatTranslationString(cmsContent.autoPlayLabel, {
      countdownDuration: 15
    });
    $[41] = cmsContent.autoPlayLabel;
    $[42] = formatTranslationString;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const t18 = `${t16}. ${t17}: ${title}.`;
  let t19;
  if ($[44] !== cancelButtonRef || $[45] !== onClose || $[46] !== t18) {
    t19 = <div><Button aria-label={t18} Icon={CloseIcon} id="autoplay-cancel-button" isSmall={true} onClick={onClose} ref={cancelButtonRef} /></div>;
    $[44] = cancelButtonRef;
    $[45] = onClose;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t10 || $[49] !== t15 || $[50] !== t19 || $[51] !== t5 || $[52] !== t6 || $[53] !== t9) {
    t20 = <Card className={styles[PARENT_CLASS]} elevation={0}>{t5}{t6}{t9}{t10}{t15}{t19}</Card>;
    $[48] = t10;
    $[49] = t15;
    $[50] = t19;
    $[51] = t5;
    $[52] = t6;
    $[53] = t9;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  return t20;
};
function _temp(oldTime) {
  return oldTime - 1;
}